/**
 * railgun — 軌道炮。動詞:**停下蓄力、把敵人排成一線**。
 *
 * 玩法:武器**自動蓄力(~1.4s)→ 自動放**(K-3 友善:不需按住)。蓄力時 UI 顯示
 * 蓄力條/光點;放出一道**貫穿整條 lane**(玩家 y 那一帶)的高傷光束。深度來自
 * 「蓄力那 1.4s 把敵人排到同一條 y」,排得好一發清一整排。
 *
 * 純邏輯(蓄力進度)在 types.ts 的 advanceCharge/chargeRatio,可單測;此檔只組裝。
 * 鐵則:威力 = baseBulletDamage × 武器倍率 × 武器等級曲線,**不讀修煉值**。
 */

import type { WeaponBehavior, WeaponTickContext, ChargeState } from './types';
import {
  advanceCharge,
  chargeRatio,
  weaponLevelDamageMult,
  applyRateMod,
  spreadWidthMult,
  sizeRangeMult,
  blastRangeBonus,
  extraHitChains,
  pierceDamageFalloff,
  LANE_DAMAGE_FALLOFF,
} from './types';

export const RAILGUN_ID = 'railgun';
export const RAILGUN_BLURB = '自動蓄力後放出一道貫穿整排的光束 — 蓄力時把敵人排成一線最賺。';

/**
 * 蓄滿放一發所需秒數。
 * boss 2026-06-14 第二輪「算範圍×傷害,別又廣又高」:railgun 是 range★5(貫穿整排,
 * 最廣)的武器,單發 DPS **不該也最高**。1.4→1.5:略拉長蓄力 = 降單體 DPS,讓 range★5
 * 收進帶內(配合 DAMAGE_MULT 6→4.6)。可躲鐵則不動子彈速度/招式,只調自身節奏。
 */
export const RAILGUN_CHARGE_SEC = 1.5;
/** 光束覆蓋的半高(px):玩家 y ± 這個範圍內的敵人都吃傷(一整條 lane)。 */
export const RAILGUN_BEAM_HALF_HEIGHT = 28;
/**
 * 高傷倍率。boss 2026-06-14 第二輪:6→4.6。railgun 範圍★5(整排 lane,最廣)+ 原本
 * 單體 DPS 也最高 = 典型「又廣又高」離群(中庸 ~116 DPS 遠超 baseline 84,把王秒了)。
 * 下修到 4.6 讓其單體 DPS 收斂回 baseline 連發砲那條帶內;range★5 的價值改由「一發排好
 * 清整排」體現,不靠單發爆高。仍是慢蓄力高傷武器,只是不再離群。
 */
export const RAILGUN_DAMAGE_MULT = 4.6;
/** 「多一道 beam」(穿透/彈跳詮釋)時,額外光束相對玩家 y 的上下偏移(px)。 */
export const RAILGUN_EXTRA_BEAM_OFFSET = 64;

export class RailgunBehavior implements WeaponBehavior {
  readonly id = RAILGUN_ID;
  private _charge: ChargeState = { elapsedSec: 0 };

  tick(ctx: WeaponTickContext): void {
    // 三選一詮釋 — railgun 版:
    //   攻擊增速 → 蓄力更快(chargeSec × effectiveAutoFireMult,clamp 下限)。
    //   多顆子彈 → 光束加寬(half-height ×(1+0.25n))。
    //   巨彈/爆裂 → 光束再加寬(判定帶更厚:×bulletSizeMult + spreadBlastRadius px)。
    //   穿透/彈跳 → 多放幾道平行 beam(上下偏移的額外 lane)。
    const m = ctx.mods;
    const chargeSec = applyRateMod(RAILGUN_CHARGE_SEC, m.effectiveAutoFireMult());

    const res = advanceCharge(this._charge, ctx.dt, chargeSec);
    this._charge.elapsedSec = res.elapsedSec;

    // 蓄力 UI(光點/條)。headless 缺 seam 時略過。
    ctx.setChargeRatio?.(chargeRatio(res.elapsedSec, chargeSec));

    if (!res.fired) return;

    // 蓄滿 → 放一道貫穿 lane 的光束。威力 = 基礎傷 × railgun 倍率 × 等級曲線。
    const dmg = Math.round(
      ctx.baseBulletDamage() * RAILGUN_DAMAGE_MULT * weaponLevelDamageMult(ctx.weaponLevel),
    );
    // 光束半高:散彈加寬 × 巨彈倍率 + 爆裂帶寬。
    const halfHeight =
      RAILGUN_BEAM_HALF_HEIGHT
        * spreadWidthMult(m.effectiveSpreadBullets())
        * sizeRangeMult(m.bulletSizeMult)
      + blastRangeBonus(m.spreadBlastRadius, 0.5);
    // 穿透/彈跳 → 多幾道平行光束(額外 lane,各偏移 RAILGUN_EXTRA_BEAM_OFFSET)。
    const extraBeams = extraHitChains(m.pierceCount, m.bounceCount + m.evoBounceBonus);

    if (ctx.fireBeam) {
      // 主 beam 滿傷(railgun = 單體高傷武器)。額外平行 beam(穿透/彈跳詮釋的「廣」)
      // 吃 LANE_DAMAGE_FALLOFF^N 衰減(boss 2026-06-14「不能打得又廣傷害又高」):
      // 多 beam 把更多敵人排進來打,但每多一條後段傷遞減,廣不等於滿傷。
      ctx.fireBeam(ctx.playerY, halfHeight, dmg);
      for (let i = 1; i <= extraBeams; i++) {
        const sign = i % 2 === 1 ? -1 : 1;
        const step = Math.ceil(i / 2);
        const beamDmg = pierceDamageFalloff(dmg, i, LANE_DAMAGE_FALLOFF);
        ctx.fireBeam(ctx.playerY + sign * step * RAILGUN_EXTRA_BEAM_OFFSET, halfHeight, beamDmg);
      }
    } else {
      // 無 beam seam 的退化路徑(理論上 scene 一定帶):用一發超大穿透子彈近似,
      // 仍走既有子彈管線,確保任何 harness 下 railgun 都「會放東西」。
      ctx.spawnBullet(dmg, 0, 2.2 * sizeRangeMult(m.bulletSizeMult));
    }
  }

  reset(): void {
    this._charge.elapsedSec = 0;
  }

  /** 測試/UI 用:目前蓄力比(0..1,以「無 mod」基準秒數算,給 UI 蓄力條)。 */
  chargeRatio(): number {
    return chargeRatio(this._charge.elapsedSec, RAILGUN_CHARGE_SEC);
  }
}
